import * as React from 'react'
import { useForm } from 'react-hook-form'
import { Form } from '@/components/ui/form'
import AccountCombobox from '@/features/accounts/components/AccountCombobox'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { RotateCcw } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { format } from 'date-fns'

type Props = {
    accountId?: string
    onAccountChange: (accountId: string) => void
    scope: 'all' | 'account' | 'store'
    onScopeChange: (scope: 'all' | 'account' | 'store') => void
    renewalRange?: { from: Date | null; to: Date | null }
    onRenewalRangeChange?: (range: { from: Date | null; to: Date | null }) => void
    search?: string
    onSearchChange?: (value: string) => void
    billing?: 'all' | 'direct' | 'fastbound'
    onBillingChange?: (billing: 'all' | 'direct' | 'fastbound') => void
}

type FilterForm = {
    accountId: string
}

export default function SubscriptionsTableFilter({ accountId, onAccountChange, scope, onScopeChange, renewalRange, onRenewalRangeChange, search, onSearchChange, billing, onBillingChange }: Props) {
    const form = useForm<FilterForm>({ defaultValues: { accountId: accountId ?? '' } })

    React.useEffect(() => {
        form.setValue('accountId', accountId ?? '', { shouldDirty: false })
    }, [accountId])

    React.useEffect(() => {
        const sub = form.watch((values) => {
            onAccountChange(values.accountId || '')
        })
        return () => sub.unsubscribe()
    }, [form, onAccountChange])

    return (
        <div className='flex flex-wrap items-end gap-3 mb-3'>
            <div className='w-80'>
                <div className='flex flex-col gap-1'>
                    <Label htmlFor='search'>Search</Label>
                    <Input
                        id='search'
                        placeholder='Search subscriptions'
                        value={search ?? ''}
                        onChange={(e) => onSearchChange?.(e.target.value)}
                    />
                </div>
            </div>
            <div className='w-64'>
                <Form {...form}>
                    <AccountCombobox label='Account' name='accountId' />
                </Form>
            </div>

            <div className='w-48'>
                <div className='flex flex-col gap-1'>
                    <Label htmlFor='scope'>Scope</Label>
                    <Select value={scope} onValueChange={(v) => onScopeChange(v as Props['scope'])}>
                        <SelectTrigger id='scope'>
                            <SelectValue placeholder='All' />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value='all'>All</SelectItem>
                            <SelectItem value='account'>Account</SelectItem>
                            <SelectItem value='store'>Store</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className='w-48'>
                <div className='flex flex-col gap-1'>
                    <Label htmlFor='billing'>Billing</Label>
                    <Select value={billing ?? 'all'} onValueChange={(v) => onBillingChange?.(v as 'all' | 'direct' | 'fastbound')}>
                        <SelectTrigger id='billing'>
                            <SelectValue placeholder='All' />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value='all'>All</SelectItem>
                            <SelectItem value='direct'>Direct</SelectItem>
                            <SelectItem value='fastbound'>FastBound</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className='w-72'>
                <div className='flex flex-col gap-1'>
                    <Label>Renewal date</Label>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant='outline' className='justify-start text-left font-normal'>
                                {renewalRange?.from ? (
                                    renewalRange.to ? (
                                        `${format(renewalRange.from, 'LLL d, y')} - ${format(renewalRange.to, 'LLL d, y')}`
                                    ) : (
                                        format(renewalRange.from, 'LLL d, y')
                                    )
                                ) : (
                                    <span>Pick a date range</span>
                                )}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className='w-auto p-0' align='start'>
                            <Calendar
                                mode='range'
                                selected={{ from: renewalRange?.from || undefined, to: renewalRange?.to || undefined } as any}
                                onSelect={(range: any) => onRenewalRangeChange?.({ from: range?.from ?? null, to: range?.to ?? null })}
                                numberOfMonths={2}
                            />
                        </PopoverContent>
                    </Popover>
                </div>
            </div>
            <div className='ml-auto'>
                <Button
                    type='button'
                    variant='outline'
                    onClick={() => {
                        form.reset({ accountId: '' })
                        onAccountChange('')
                        onScopeChange('all')
                        onRenewalRangeChange?.({ from: null, to: null })
                        onSearchChange?.('')
                        onBillingChange?.('all')
                    }}
                >
                    <RotateCcw className='h-4 w-4 mr-2' />
                    Clear filters
                </Button>
            </div>

        </div>
    )
}


